import { useState } from 'react';
import {
  Ban,
  AlertTriangle,
  Loader2,
  ShieldAlert,
  Package,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { useCurrency } from '@/hooks/useCurrency';
import { useOwner2FAApproval } from '@/hooks/useOwner2FAApproval';

interface Order {
  id: string;
  order_number: string;
  total: number;
  status: string;
}

interface VoidOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  onVoidOrder: (data: {
    orderId: string;
    reason: string;
    approvedBy: string;
    restoreInventory: boolean;
  }) => Promise<void>;
  isProcessing?: boolean;
}

const voidReasons = [
  'Customer request',
  'Wrong order',
  'Kitchen mistake',
  'Item unavailable',
  'Duplicate order',
  'System error',
  'Other',
];

export function VoidOrderDialog({
  open,
  onOpenChange,
  order,
  onVoidOrder,
  isProcessing = false,
}: VoidOrderDialogProps) {
  const { formatCurrency } = useCurrency();
  const { requireApproval, owner2FAEnabled } = useOwner2FAApproval();
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [restoreInventory, setRestoreInventory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPinDialog, setShowPinDialog] = useState(false);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      // Reset state when opening
      setReason('');
      setCustomReason('');
      setRestoreInventory(false);
      setError(null);
    }
    onOpenChange(isOpen);
  };

  const handleInitiateVoid = () => {
    const finalReason = reason === 'Other' ? customReason : reason;

    if (!finalReason.trim()) {
      setError('Please select or enter a reason for voiding');
      return;
    }

    setError(null);
    setShowPinDialog(true);
  };

  const executeVoid = async (managerName: string) => {
    if (!order) return;

    const finalReason = reason === 'Other' ? customReason : reason;

    try {
      await onVoidOrder({
        orderId: order.id,
        reason: finalReason,
        approvedBy: managerName,
        restoreInventory,
      });
      handleOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to void order');
    }
  };

  const handlePinVerified = async (_managerId: string, managerName: string) => {
    if (!order) return;

    // Check if owner 2FA is required for this void
    if (owner2FAEnabled) {
      const approved = await requireApproval({
        actionType: 'void_order',
        metadata: {
          orderId: order.id,
          orderNumber: order.order_number,
          amount: order.total,
          reason: reason === 'Other' ? customReason : reason,
          managerName,
        },
        description: `Void Order #${order.order_number} (${formatCurrency(order.total)})`,
        amount: order.total,
      });

      if (approved) {
        await executeVoid(managerName);
      } else {
        setError('Owner 2FA approval denied');
      }
    } else {
      // No owner 2FA required, proceed directly
      await executeVoid(managerName);
    }
  };

  if (!order) return null;

  return (
    <>
      <ResponsiveModal open={open} onOpenChange={handleOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Ban className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <ResponsiveModalTitle>Void Order</ResponsiveModalTitle>
                <ResponsiveModalDescription>
                  Order #{order.order_number} • {formatCurrency(order.total)}
                </ResponsiveModalDescription>
              </div>
            </div>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-6">
            {/* Warning Banner */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  This action requires manager approval
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  Voiding an order will cancel all items and kitchen tickets. This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Reason Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Reason for Voiding <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {voidReasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className={`p-3 text-sm text-left rounded-lg border-2 transition-all ${
                      reason === r
                        ? 'border-red-500 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300'
                        : 'border-border hover:border-red-200 hover:bg-red-50/50 dark:hover:bg-red-950/10'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Reason */}
            {reason === 'Other' && (
              <div>
                <Label htmlFor="custom-reason" className="text-sm font-medium">
                  Please specify <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="custom-reason"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter reason for voiding..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>
            )}

            {/* Inventory Restore Option */}
            <div className="flex items-start gap-3 p-4 border rounded-lg">
              <Checkbox
                id="restore-inventory"
                checked={restoreInventory}
                onCheckedChange={(checked) => setRestoreInventory(checked === true)}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="restore-inventory"
                  className="text-sm font-medium cursor-pointer flex items-center gap-2"
                >
                  <Package className="h-4 w-4" />
                  Restore inventory for voided items
                </Label>
                <p className="text-xs text-muted-foreground">
                  Check this if items were not prepared/consumed and should be returned to inventory
                </p>
              </div>
            </div>

            {/* Order Impact Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                What will happen:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Order will be marked as cancelled</li>
                <li>All items will be voided</li>
                <li>Kitchen tickets will be removed from KDS</li>
                {restoreInventory && <li className="text-green-600 dark:text-green-400">Inventory quantities will be restored</li>}
                <li>Action will be logged to audit trail</li>
              </ul>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
                {error}
              </p>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => handleOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleInitiateVoid}
              disabled={isProcessing || !reason || (reason === 'Other' && !customReason.trim())}
              className="gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Voiding...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4" />
                  Void Order
                </>
              )}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Manager PIN Dialog */}
      <ManagerPinDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onVerified={handlePinVerified}
        title="Manager Approval Required"
        description="Enter manager PIN to void this order"
      />
    </>
  );
}

